import React, { useCallback, useState } from 'react';

interface LazyImageProps {
  src?: string | null;
  alt: string;
  /** Classe do wrapper: é ele que reserva o espaço e recebe o skeleton. */
  className?: string;
  /** Classe da própria <img> (object-cover/contain, raio, etc.). */
  imgClassName?: string;
  /** O que aparece sem `src` ou quando o carregamento falha. */
  fallback?: React.ReactNode;
  /** Acima da dobra: carrega junto da página em vez de esperar o scroll. */
  eager?: boolean;
  onClick?: () => void;
}

/**
 * Imagem com carregamento preguiçoso, skeleton no lugar reservado e fade ao
 * chegar. Substitui as 23 `<img>` cruas do app, nenhuma das quais tinha
 * `loading`, `decoding`, `onLoad` ou tratamento de erro.
 *
 * O lazy é o nativo do navegador (`loading="lazy"`), não IntersectionObserver.
 *
 * Nota: as URLs de foto são signed URLs do Supabase e expiram (~1h) — por isso
 * o `onError` cai no fallback em vez de deixar o ícone de imagem quebrada.
 */
export default function LazyImage({
  src,
  alt,
  className = '',
  imgClassName = '',
  fallback = null,
  eager = false,
  onClick,
}: LazyImageProps) {
  const [estado, setEstado] = useState<'carregando' | 'pronto' | 'erro'>('carregando');

  // Imagem que já está em cache dispara o `load` antes de o React ligar o
  // handler. Sem esta checagem no ref ela ficaria em opacity-0 para sempre.
  const ref = useCallback((el: HTMLImageElement | null) => {
    if (el && el.complete && el.naturalWidth > 0) setEstado('pronto');
  }, []);

  if (!src || estado === 'erro') {
    return (
      <div className={`flex items-center justify-center ${className}`} onClick={onClick}>
        {fallback}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} onClick={onClick}>
      {estado === 'carregando' && (
        <span aria-hidden="true" className="skeleton absolute inset-0 rounded-[inherit]" />
      )}
      <img
        ref={ref}
        src={src}
        alt={alt}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        onLoad={() => setEstado('pronto')}
        onError={() => setEstado('erro')}
        className={`transition-opacity duration-300 ${estado === 'pronto' ? 'opacity-100' : 'opacity-0'} ${imgClassName}`}
      />
    </div>
  );
}
